import { httpsCallable } from 'firebase/functions'

import { functions } from '../../../firebase'

// Types for text analytics API response
export interface TextAnalyticsEntry {
  id: string
  coreEmotion: string
  text: string
  characterCount: number
}

export interface TextAnalyticsResult {
  totalCount: number
  totalCharacters: number
  entries: TextAnalyticsEntry[]
}

// Types for database analytics API response
export interface DatabaseAnalyticsResult {
  totalDocuments: number
  coreEmotionCounts: Record<string, number>
  emotionTagCounts: Record<string, number>
  documentsWithText: number
  documentsWithGifUrl: number
  documentsWithSource: number
  lastUpdated: string
}

interface TextAnalyticsResponse {
  success: boolean
  data?: TextAnalyticsResult
  error?: string
}

interface DatabaseAnalyticsResponse {
  success: boolean
  data?: DatabaseAnalyticsResult
  error?: string
}

// Types for similarity analysis
export interface SimilarityMatch {
  tag1: string
  tag2: string
  similarityScore: number
  similarityType: 'exact' | 'phonetic' | 'semantic' | 'stem'
  algorithm: string
  count1: number
  count2: number
  combinedCount: number
}

export interface SimilarityConfig {
  exactThreshold: number
  phoneticThreshold: number
  semanticThreshold: number
  stemThreshold: number
}

export interface SimilarityAnalysisResult {
  totalTags: number
  totalMatches: number
  matches: SimilarityMatch[]
  config: SimilarityConfig
  analysisDate: string
}

interface SimilarityAnalysisResponse {
  success: boolean
  data?: SimilarityAnalysisResult
  error?: string
}

// Types for merge operation
export interface MergeResult {
  documentsUpdated: number
  documentsProcessed: number
  message: string
}

interface MergeResponse {
  success: boolean
  data?: MergeResult
  error?: string
}

// Types for gif count API response
export interface GifCountResult {
  totalCount: number
}

interface GifCountResponse {
  success: boolean
  data?: GifCountResult
  error?: string
}

export class AnalyticsService {
  /**
   * Get text analytics from all GIF documents with non-empty text
   * @returns Promise<TextAnalyticsResult> - Analytics data including count, characters, and entries
   */
  static async getTextAnalytics(): Promise<TextAnalyticsResult> {
    try {
      const getTextAnalyticsFunction = httpsCallable<
        Record<string, never>,
        TextAnalyticsResponse
      >(functions, 'getTextAnalytics')

      const result = await getTextAnalyticsFunction({})

      if (result.data.success && result.data.data) {
        return result.data.data
      } else {
        throw new Error(result.data.error || 'Failed to get text analytics')
      }
    } catch (error) {
      console.error('Error calling getTextAnalytics:', error)
      throw error
    }
  }

  /**
   * Get database analytics including counts by coreEmotion and overall metrics
   * @returns Promise<DatabaseAnalyticsResult> - Analytics data including document counts and breakdowns
   */
  static async getDatabaseAnalytics(): Promise<DatabaseAnalyticsResult> {
    try {
      const getDatabaseAnalyticsFunction = httpsCallable<
        Record<string, never>,
        DatabaseAnalyticsResponse
      >(functions, 'getDatabaseAnalytics')

      const result = await getDatabaseAnalyticsFunction({})

      if (result.data.success && result.data.data) {
        return result.data.data
      } else {
        throw new Error(result.data.error || 'Failed to get database analytics')
      }
    } catch (error) {
      console.error('Error calling getDatabaseAnalytics:', error)
      throw error
    }
  }

  /**
   * Helper method to calculate analytics summaries
   */
  static calculateTextAnalyticsSummary(data: TextAnalyticsResult) {
    const emotionCounts: Record<string, number> = {}
    let totalWords = 0

    data.entries.forEach((entry) => {
      emotionCounts[entry.coreEmotion] =
        (emotionCounts[entry.coreEmotion] || 0) + 1
      totalWords += entry.text.split(/\s+/).length
    })

    const avgCharactersPerEntry =
      data.totalCount > 0
        ? Math.round(data.totalCharacters / data.totalCount)
        : 0
    const avgWordsPerEntry =
      data.totalCount > 0 ? Math.round(totalWords / data.totalCount) : 0

    return {
      emotionCounts,
      totalWords,
      avgCharactersPerEntry,
      avgWordsPerEntry,
    }
  }

  /**
   * Helper method to format coreEmotion counts for display
   * Backend now provides complete data including zero counts
   */
  static formatCoreEmotionCounts(coreEmotionCounts: Record<string, number>) {
    const entries = Object.entries(coreEmotionCounts)
      .sort(([, a], [, b]) => b - a) // Sort by count descending
      .map(([emotion, count]) => ({
        emotion:
          emotion === 'unknown'
            ? 'Unknown'
            : emotion.charAt(0).toUpperCase() + emotion.slice(1),
        count,
      }))

    const total = entries.reduce((sum, entry) => sum + entry.count, 0)

    return entries.map((entry) => ({
      ...entry,
      percentage: total > 0 ? Math.round((entry.count / total) * 100) : 0,
    }))
  }

  /**
   * Helper method to format emotion tag counts for display
   * Returns entries sorted alphabetically by tag name
   */
  static formatEmotionTagCounts(emotionTagCounts: Record<string, number>) {
    const entries = Object.entries(emotionTagCounts)
      .sort(([a], [b]) => a.localeCompare(b)) // Sort alphabetically by tag name
      .map(([tag, count]) => ({
        tag,
        count,
      }))

    const total = entries.reduce((sum, entry) => sum + entry.count, 0)

    return entries.map((entry) => ({
      ...entry,
      percentage: total > 0 ? Math.round((entry.count / total) * 100) : 0,
    }))
  }

  /**
   * Get semantic similarity analysis for emotional tags
   * @param config Optional configuration for similarity thresholds
   * @returns Promise<SimilarityAnalysisResult> - Similarity analysis results
   */
  static async getEmotionalTagSimilarity(
    config?: Partial<SimilarityConfig>
  ): Promise<SimilarityAnalysisResult> {
    try {
      const getSimilarityFunction = httpsCallable<
        { config?: Partial<SimilarityConfig> },
        SimilarityAnalysisResponse
      >(functions, 'getEmotionalTagSimilarity')

      const result = await getSimilarityFunction({ config })

      if (result.data.success && result.data.data) {
        return result.data.data
      } else {
        throw new Error(
          result.data.error || 'Failed to get similarity analysis'
        )
      }
    } catch (error) {
      console.error('Error calling getEmotionalTagSimilarity:', error)
      throw error
    }
  }

  /**
   * Helper method to format similarity matches for display
   * Groups matches by similarity type and sorts by score
   */
  static formatSimilarityMatches(matches: SimilarityMatch[]) {
    // Group by similarity type
    const groupedMatches = matches.reduce(
      (groups, match) => {
        const type = match.similarityType
        if (!groups[type]) {
          groups[type] = []
        }
        groups[type].push(match)
        return groups
      },
      {} as Record<string, SimilarityMatch[]>
    )

    // Sort each group by similarity score (highest first)
    Object.keys(groupedMatches).forEach((type) => {
      groupedMatches[type].sort((a, b) => b.similarityScore - a.similarityScore)
    })

    return groupedMatches
  }

  /**
   * Generate merge recommendations from similarity matches
   * Filters for high-confidence matches that could be merged
   */
  static generateMergeRecommendations(matches: SimilarityMatch[]) {
    // Filter for high-confidence matches (90%+ similarity)
    const highConfidenceMatches = matches.filter(
      (match) => match.similarityScore >= 0.9
    )

    // Group by tag pairs and deduplicate
    const recommendations = new Map<string, SimilarityMatch>()

    highConfidenceMatches.forEach((match) => {
      const key = [match.tag1, match.tag2].sort().join('|')
      const existing = recommendations.get(key)

      // Keep the highest scoring match for each tag pair
      if (!existing || match.similarityScore > existing.similarityScore) {
        recommendations.set(key, match)
      }
    })

    return Array.from(recommendations.values()).sort(
      (a, b) => b.similarityScore - a.similarityScore
    )
  }

  /**
   * Merge two emotional tags by replacing all instances of sourceTag with targetTag
   * @param sourceTag The tag to be replaced
   * @param targetTag The tag to replace with
   * @returns Promise<MergeResult> - Result of the merge operation
   */
  static async mergeEmotionalTags(
    sourceTag: string,
    targetTag: string
  ): Promise<MergeResult> {
    try {
      const mergeFunction = httpsCallable<
        { sourceTag: string; targetTag: string },
        MergeResponse
      >(functions, 'mergeEmotionalTags')

      const result = await mergeFunction({ sourceTag, targetTag })

      if (result.data.success && result.data.data) {
        return result.data.data
      } else {
        throw new Error(result.data.error || 'Failed to merge emotional tags')
      }
    } catch (error) {
      console.error('Error calling mergeEmotionalTags:', error)
      throw error
    }
  }

  /**
   * Get total count of GIFs in the database
   * @returns Promise<number> - Total number of GIF documents
   */
  static async getGifCount(): Promise<number> {
    try {
      const getGifCountFunction = httpsCallable<
        Record<string, never>,
        GifCountResponse
      >(functions, 'getGifCount')

      const result = await getGifCountFunction({})

      if (result.data.success && result.data.data) {
        return result.data.data.totalCount
      } else {
        throw new Error(result.data.error || 'Failed to get gif count')
      }
    } catch (error) {
      console.error('Error calling getGifCount:', error)
      throw error
    }
  }
}
